import type {
  CancelOrderPayload,
  DeliveryFailedPayload,
  ListOrdersParams,
  PhysicalOrdersResponse,
  PhysicalOrdersSummary,
  SaveTrackingPayload,
  SummaryOrdersParams,
  UpdateStatusPayload
} from "@/@types/Orders";
import { api } from "./api";

function buildOrdersQueryParams(params: SummaryOrdersParams): URLSearchParams {
  const queryParams = new URLSearchParams();

  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.q) queryParams.append('q', params.q);
  if (params.currency) queryParams.append('currency', params.currency);
  if ('page' in params && params.page) {
    queryParams.append('page', params.page.toString());
  }

  params.status?.forEach(s => queryParams.append('status[]', s));
  params.processing_status?.forEach(s => queryParams.append('processing_status[]', s));
  params.payment_methods?.forEach(m => queryParams.append('payment_methods[]', m));
  params.products?.forEach(p => queryParams.append('products[]', p));
  params.offers?.forEach(o => queryParams.append('offers[]', o));
  params.affiliates?.forEach(a => queryParams.append('affiliates[]', a));

  return queryParams;
}

// Re-exporta tipos para compatibilidade
export type {
  CancelOrderPayload,
  DeliveryFailedPayload,
  DeliveryTracking,
  ListOrdersParams,
  PhysicalOrder,
  PhysicalOrdersResponse,
  PhysicalOrdersSummary,
  ProcessingStatus,
  SaveTrackingPayload,
  ShippingAddress,
  SummaryOrdersParams,
  UpdateStatusPayload
} from "@/@types/Orders";

// Re-exporta helpers para compatibilidade
export {
  canTransitionTo,
  getAllowedTransitions,
  STATUS_TRANSITIONS
} from "@/utils/orders";

export const PhysicalOrdersService = {
  /**
   * Lista pedidos físicos do usuário autenticado
   * GET /api/v1/physical-orders
   *
   * @param params - Parâmetros de filtro e paginação
   */
  list: async (params: ListOrdersParams): Promise<PhysicalOrdersResponse> => {
    const queryString = buildOrdersQueryParams(params).toString();
    const url = queryString ? `/v1/physical-orders?${queryString}` : '/v1/physical-orders';

    const response = await api.get(url);
    return response.data;
  },

  /**
   * Contagens agregadas para os cards de resumo
   * GET /api/v1/physical-orders/summary
   */
  summary: async (params: SummaryOrdersParams): Promise<PhysicalOrdersSummary> => {
    const queryString = buildOrdersQueryParams(params).toString();
    const url = queryString
      ? `/v1/physical-orders/summary?${queryString}`
      : '/v1/physical-orders/summary';

    const response = await api.get(url);
    return response.data;
  },

  /**
   * Adiciona ou atualiza código de rastreamento de um pedido físico
   * POST /api/v1/physical-orders/{id}/tracking
   *
   * @param orderId - UUID do pedido (sem o #)
   * @param payload - Dados do rastreamento (código, transportadora, URL opcional)
   */
  saveTracking: async (orderId: string, payload: SaveTrackingPayload) => {
    const response = await api.post(`/v1/physical-orders/${orderId}/tracking`, payload);
    return response.data;
  },

  /**
   * Atualiza o status de processamento de um pedido físico
   * PATCH /api/v1/physical-orders/{id}/status
   *
   * Use canTransitionTo() para verificar se a transição é válida antes de chamar
   *
   * @param orderId - UUID do pedido (sem o #)
   * @param payload - Novo status de processamento
   */
  updateStatus: async (orderId: string, payload: UpdateStatusPayload) => {
    const response = await api.patch(`/v1/physical-orders/${orderId}/status`, payload);
    return response.data;
  },

  /**
   * Marca pedido como falha na entrega
   * POST /api/v1/physical-orders/{id}/delivery-failed
   *
   * @param orderId - UUID do pedido (sem o #)
   * @param payload - Motivo da falha na entrega
   */
  markDeliveryFailed: async (orderId: string, payload: DeliveryFailedPayload) => {
    const response = await api.post(`/v1/physical-orders/${orderId}/delivery-failed`, payload);
    return response.data;
  },

  /**
   * Cancela ou devolve o pedido
   * POST /api/v1/physical-orders/{id}/cancel
   *
   * @param orderId - UUID do pedido
   * @param payload - Status (cancelled ou returned) e endereço de retorno opcional
   */
  cancelOrder: async (orderId: string, payload: CancelOrderPayload) => {
    const response = await api.post(`/v1/physical-orders/${orderId}/cancel`, payload);
    return response.data;
  },
};
